import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux'
import { 
    createParis,
    getGuias,
    getDailyReception,
    updateSubestadosBulk,
    getOrphanItems,
    findMatchesForItems,
    reconcileItems,
    exportDailyReceptionExcel
} from '@apis/mantenedores/guias';

/**
 * Hook para manejar el mantenedor de guías
 * Maneja filtros, live search y llamadas a la API
 */


export const useGuias = () => {
    const user = useSelector(state => state.auth.user)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // Estados adicionales para diferentes tipos de datos
    const [guias, setGuias] = useState([]);
    const [pagination, setPagination] = useState({});
    const [dailyReception, setDailyReception] = useState([]);
    const [estadisticas, setEstadisticas] = useState({});
    const [orphanItems, setOrphanItems] = useState([]);
    const [orphanSummary, setOrphanSummary] = useState({});
    const [matches, setMatches] = useState({});
    const [reconciliationResult, setReconciliationResult] = useState({});

    const handleCreateParis = async (parisData, file) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await createParis(user, parisData, file);

        if (response.success) {
            setSuccess(response.message);
        } else {
            setError(response.error);
        }

        setLoading(false);
        return response;
    };

    const handleGetGuias = async (params = {}) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await getGuias(user, params);

        if (response.success) {
            setGuias(response.data);
            setPagination(response.pagination);
            setSuccess(response.message);
        } else {
            setError(response.error);
        }

        setLoading(false);
        return response;
    };

    const handleGetDailyReception = async (params = {}) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await getDailyReception(user, params);

        if (response.success) {
            setDailyReception(response.data);
            setEstadisticas(response.estadisticas);
            setSuccess(response.message);
        } else {
            setError(response.error);
        }

        setLoading(false);
        return response;
    };

    const handleUpdateSubestadosBulk = async (updates) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await updateSubestadosBulk(user, updates);

        if (response.success) {
            setSuccess(response.message);
        } else {
            setError(response.error);
        }

        setLoading(false);
        return response;
    };

    const handleGetOrphanItems = async (params = {}) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await getOrphanItems(user, params);

        if (response.success) {
            setOrphanItems(response.data);
            setOrphanSummary(response.summary);
            setSuccess(response.message);
        } else {
            setError(response.error);
        }

        setLoading(false);
        return response;
    };

    const handleFindMatchesForItems = async (data) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await findMatchesForItems(user, data);

        if (response.success) {
            setMatches(response.data);
            setSuccess(response.message);
        } else {
            setError(response.error);
        }

        setLoading(false);
        return response;
    };

    const handleReconcileItems = async (data) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await reconcileItems(user, data);

        if (response.success) {
            setReconciliationResult(response.data);
            setSuccess(response.message);
        } else {
            setError(response.error);
        }

        setLoading(false);
        return response;
    };

    const handleExportDailyReceptionExcel = async (params = {}) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await exportDailyReceptionExcel(user, params);

        if (response.success) {
            // Crear un blob y descargar el archivo
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', response.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            setSuccess(response.message);
        } else {
            setError(response.error);
        }

        setLoading(false);
        return response;
    };

    return {
        loading,
        error,
        success,
        // Estados de datos
        guias,
        pagination,
        dailyReception,
        estadisticas,
        orphanItems,
        orphanSummary,
        matches,
        reconciliationResult,
        // Funciones del hook
        handleCreateParis,
        handleGetGuias,
        handleGetDailyReception,
        handleUpdateSubestadosBulk,
        handleGetOrphanItems,
        handleFindMatchesForItems,
        handleReconcileItems,
        handleExportDailyReceptionExcel
    };
};
